import * as React from 'react'
import { backTen, forwardTen } from '../../helpers/historyHelper.ts'
import { historyState, setHistoryStateUpdate } from '../../store/historyState.ts'
import { DateButton } from './components/DateButton.tsx'
import { IPet } from '../../interfaces/types.ts'
import { PetPickerModal } from '../../forms/v2/components/PetPickerModal.tsx'
import { useSnapshot } from 'valtio'
import { dataState } from '../../store/dataState.ts'
import { controlState } from '../../store/controlState.ts'
import { centerMap } from '../map/mapHelpers.tsx'
import { HistoryToggles } from './components/HistoryToggles.tsx'
import { dayHistoryState } from '../../store/dayHistoryState.ts'

interface Props {
}
const MAX_LOCATIONS = 10

export const HistoryButtons: React.FC<Props> = () => {
    const startDate = (useSnapshot(historyState) as typeof historyState).startDate
    const endDate = (useSnapshot(historyState) as typeof historyState).endDate
    const reachedStart = (useSnapshot(historyState) as typeof historyState).reachedStart
    const locations = (useSnapshot(historyState) as typeof historyState).locations
    const isMovieMode = (useSnapshot(historyState) as typeof historyState).isMovieMode
    const pets = (useSnapshot(dataState) as typeof dataState).pets
    const selectedPetId = (useSnapshot(historyState) as typeof historyState).selectedPetId
    const isMobile = useSnapshot(controlState).isOnMobileDevice
    const dayHistoryActive = (useSnapshot(dayHistoryState) as typeof dayHistoryState).active
    const [curJump, setCurJump] = React.useState<number>(0)
    const map = (useSnapshot(historyState) as typeof historyState).map

    const noHistory = selectedPetId && isMobile && !locations?.length

    const empty = (<div className={'text-primary bg-primary-content text-xl mt-4'}>No history available</div> )

    const getNextJump = (cur: number, direction: 'up' | 'down') => {
        let index =  cur - 1
        if (direction === 'up') {
            index = (index + 1) % MAX_LOCATIONS
        } else if (direction === 'down') {
            index = (index - 1 + MAX_LOCATIONS) % MAX_LOCATIONS
        }
        centerMap(locations[index], map!)
        return index + 1
    }

    React.useEffect(() => {
        setCurJump(0)
    }, [locations])

    const jumpControl = (
        <div className={'rounded-l-full rounded-r-full p-2 w-[50%] bg-white text-secondary mt-6 ml-auto mr-auto h-fit text-xl border-2 border-secondary'}>
            <div className={'flex items-center justify-between'}>
                <div className={'text-secondary text-3xl font-semibold'}
                     onClick={() => setCurJump(getNextJump(curJump, 'down'))}
                >-</div>
                {curJump !== 0 ? curJump : 'pan to location'}
                <div className={'text-secondary text-3xl font-semibold'}
                     onClick={() => setCurJump(getNextJump(curJump, 'up'))}
                >+</div>
            </div>
        </div>
    )

    const controls = (
        <div id={'history-buttons'} className={'flex flex-row w-[100vw] justify-between px-4'}>
            <DateButton direction={'newer'}
                        reachedStart={reachedStart}
                        historyAvailable={locations.length}
                        backwardTen={async (e) => {
                            const stateUpdate = await backTen(e)
                            setHistoryStateUpdate(stateUpdate)
                        }}
                        forwardTen={async (e) => {
                            const stateUpdate = await forwardTen(e)
                            setHistoryStateUpdate(stateUpdate)
                        }}
                        selectingDate={false}
                        date={startDate}
            />
            {isMobile ? <PetPickerModal
                onChange={(p: IPet | null) => {
                    historyState.selectedPetId = p?._id || ''
                }}
                pet={pets.find(p => p._id === selectedPetId) || null}
            /> : null}
            <DateButton direction={'older'}
                        reachedStart={reachedStart}
                        historyAvailable={locations.length}
                        backwardTen={async (e) => {
                            const stateUpdate = await backTen(e)
                            setHistoryStateUpdate(stateUpdate)
                        }}
                        forwardTen={async (e) => {
                            const stateUpdate = await forwardTen(e)
                            setHistoryStateUpdate(stateUpdate)
                        }}
                        selectingDate={false}
                        date={endDate}
            />
        </div>
    )

    const container = (
        <div className={''}>
            {!dayHistoryActive && controls}
            <div className={'flex justify-center mt-2'}>
                <HistoryToggles />
            </div>
            {!dayHistoryActive && noHistory ? empty : null}
            {!dayHistoryActive && isMobile && selectedPetId && !noHistory ? jumpControl : null}
        </div>
    )

    return !isMovieMode ? container : null
}
